import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Table, Image, Form } from "react-bootstrap";
import moment from "moment";
import {
  incidentListFetch,
  scopeUserIncidentListFetch,
  getIncidentById,
  getIncidentFormValues,
  redirectToIncidentList,
  deleteIncident,
} from "actions";
import { isEmpty } from "lodash";
import { DataTableEmptyState, DeleteModal } from "CommonComponent";
import AccessRights from "utils/AccessRights";

/* ---------- CONFIG ---------- */
const stepLabels = [
  "initiation",
  "review",
  "investigation",
  "resolution",
  "capa",
  "final",
];

const GREEN = "#1AAA55";
const GRAY = "#e5e5e5";
const DOT_SIZE = 24; // px
const TRACK_HEIGHT = 7; // px

/* ---------- PROGRESS ROW (custom, no libs) ---------- */
const ProgressRow = ({
  completion = {},
  name = "General Name",
  date = "N/A",
  timezone = "N/A",
}) => {
  // how many steps are completed (drives dots + fill)
  const completedCount = stepLabels.filter((step) => completion?.[step]).length;

  // connectors live between dots => there are (steps - 1) segments
  const totalSegments = Math.max(1, stepLabels.length - 1);
  const completedSegments = Math.max(
    0,
    Math.min(completedCount - 1, totalSegments)
  );
  const fillPct = (completedSegments / totalSegments) * 100;

  return (
    <div className="w-100">
      <div className="font-13 font-weight-medium text-dark mb-2">
        {name} | {date} | {timezone}
      </div>

      {/* Track + Dots */}
      <div style={{ position: "relative", padding: "12px 17px 28px 17px" }}>
        {/* base track */}
        <div
          style={{
            position: "relative",
            height: TRACK_HEIGHT,
            background: GRAY,
            borderRadius: TRACK_HEIGHT,
            margin: `0 ${DOT_SIZE / 2}px`, // keep track from touching first/last dots
          }}
        >
          {/* filled track */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: TRACK_HEIGHT,
              width: `${fillPct}%`,
              background: GREEN,
              borderRadius: TRACK_HEIGHT,
              transition: "width 200ms ease",
            }}
          />
        </div>

        {/* dots */}
        {stepLabels.map((label, i) => {
          let left;
          if (i === 0) {
            // first dot -> left padding
            left = 17;
          } else if (i === stepLabels.length - 1) {
            // last dot -> right padding
            left = `calc(100% - 17px)`;
          } else {
            // middle dots evenly spaced
            const pct = (i / (stepLabels.length - 1)) * 100;
            left = `calc(${pct}% + 0px)`; // no shift, track already padded
          }

          const isCompleted = !!completion?.[label];

          return (
            <div
              key={label}
              title={label}
              style={{
                position: "absolute",
                top: 14, // center vertically
                left: typeof left === "number" ? `${left}px` : left,
                transform: "translate(-50%, -50%)",
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: "50%",
                background: isCompleted ? GREEN : GRAY,
                boxShadow: "0 0 0 2px white",
              }}
            />
          );
        })}
      </div>

      {/* labels under the dots */}
      <div className="d-flex justify-content-between" style={{ marginTop: 4 }}>
        {stepLabels.map((label) => (
          <small key={label} className="text-muted" style={{ fontSize: 12 }}>
            {label.charAt(0).toUpperCase() + label.slice(1)}
          </small>
        ))}
      </div>
    </div>
  );
};

/* ---------- PAGE ---------- */
const getLastCompletedTab = (completion) => {
  for (let i = stepLabels.length - 1; i >= 0; i--) {
    if (completion?.[stepLabels[i]]) return stepLabels[i];
  }
  return stepLabels[0];
};

const OverviewList = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const session = useSelector((state) => state.session);
  const incidentState = useSelector((state) => state.incidentState);

  const [recordId, setRecordId] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchData = () => {
    if (
      session.current_page_permissions &&
      session.current_page_permissions.object_id &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      if (session.current_page_permissions.page_url === "/scopeIncident") {
        dispatch(
          scopeUserIncidentListFetch(session.current_page_permissions.object_id)
        );
      } else {
        dispatch(incidentListFetch(session.current_page_permissions.object_id));
      }
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 300000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.current_page_permissions]);

  useEffect(() => {
    if (session.current_page_permissions) {
      if (session.current_page_permissions.page_url === "/scopeIncident") {
        if (incidentState.showUpdateForm) navigate("/scopeIncident/edit");
      } else {
        if (incidentState.showUpdateForm) navigate("/incidentForm/edit");
      }
    }
  }, [incidentState.showUpdateForm]);

  useEffect(() => {
    if (incidentState.redirect_to_list) fetchData();
  }, [incidentState.redirect_to_list]);

  useEffect(() => {
    if (session.current_page_permissions.page_url === "/scopeIncident") {
      if (Array.isArray(incidentState.scopeUserIncidentList)) {
        setData(incidentState.scopeUserIncidentList);
      }
    } else {
      if (Array.isArray(incidentState.incidentList)) {
        setData(incidentState.incidentList);
      }
    }
  }, [incidentState.incidentList, incidentState.scopeUserIncidentList]);

  // Modified showUpdateForm to set active tab in localStorage
  const showUpdateForm = async (incidentId, completion) => {
    await dispatch(getIncidentFormValues(["severity_level", "scope"]));
    dispatch(getIncidentById(incidentId, "update"));

    // Determine which tab to activate based on completion
    const tabKey = getLastCompletedTab(completion);
    localStorage.setItem("incidentActiveTab", tabKey);
  };

  const handleDeleteClick = (id) => {
    setRecordId(id);
    setShowDeleteModal(true);
  };

  const deleteRecord = async () => {
    await dispatch(deleteIncident(recordId));
    setShowDeleteModal(false);
    setRecordId("");
    if (session.current_page_permissions.page_url === "/scopeIncident") {
      dispatch(
        scopeUserIncidentListFetch(session.current_page_permissions.object_id)
      );
    } else {
      dispatch(incidentListFetch(session.current_page_permissions.object_id));
    }
    dispatch(redirectToIncidentList());
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) =>
      (item.incident_name || "")
        .toLowerCase()
        .includes((filterValue || "").toLowerCase())
    );
  }, [data, filterValue]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  return (
    <div className="overview-list">
      {/* Search Input */}
      <div
        className="mb-4 position-relative bg-transparent border-0 text-color font-13 font-weight-normal"
        style={{ maxWidth: "300px" }}
      >
        <input
          type="text"
          placeholder="Search here..."
          className="bg-transparent border-0 text-color font-13 font-weight-normal"
          style={{ paddingLeft: "35px" }}
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
        />
        <Image
          src="/assets/images/search-icon.svg"
          alt="Search"
          width={16}
          height={16}
          className="position-absolute"
          style={{
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Table */}
      {isEmpty(currentData) ? (
        <DataTableEmptyState />
      ) : (
        <Table className="mb-3">
          <tbody>
            {currentData.map((item, i) => (
              <tr key={item.id || i} className="bordered-row-content">
                <td>
                  <ProgressRow
                    completion={item.completion}
                    name={item.incident_name}
                    date={
                      item.incident_datetime
                        ? moment(item.incident_datetime + "Z").format(
                            "YYYY-MM-DD hh:mm A"
                          )
                        : "N/A"
                    }
                    timezone={item.incident_timezone || "N/A"}
                  />
                </td>
                <td className="overview-actions">
                  <div>
                    {AccessRights(
                      session.current_page_permissions,
                      "edit",
                      item.created_by
                    ) &&
                      item.is_deleted === "n" && (
                        <>
                          {item.status !== "final_report" ? (
                            <Image
                              src="/assets/images/edit-icon.svg"
                              width={20}
                              className="me-2 cursor-pointer"
                              title="Edit"
                              onClick={() =>
                                showUpdateForm(item.id, item.completion)
                              }
                            />
                          ) : (
                            <Image
                              src="/assets/images/view-icon.svg"
                              width={20}
                              className="me-2 cursor-pointer"
                              title="View"
                              onClick={() =>
                                showUpdateForm(item.id, item.completion)
                              }
                            />
                          )}
                        </>
                      )}

                    {AccessRights(
                      session.current_page_permissions,
                      "delete",
                      item.created_by
                    ) &&
                      item.is_deleted === "n" &&
                      item.status !== "final_report" && (
                        <Image
                          src="/assets/images/delete-icon.svg"
                          width={20}
                          className="cursor-pointer"
                          title="Delete"
                          onClick={() => handleDeleteClick(item.id)}
                        />
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="previous-btn-dataTable me-2 d-flex align-items-center"
          >
            <Image
              src="/assets/images/arrow-previous.svg"
              width={14}
              className="me-2 mb-1"
            />
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={
                p === currentPage
                  ? "previous-btn-dataTable-active mx-1"
                  : "previous-btn-dataTable mx-1"
              }
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="previous-btn-dataTable ms-2"
          >
            Next
            <Image
              src="/assets/images/arrow-next.svg"
              width={14}
              className="ms-2 mb-1"
            />
          </button>
        </div>

        <Form.Select
          value={rowsPerPage}
          onChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          style={{ width: 90 }}
        >
          {[5, 10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Form.Select>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onDelete={deleteRecord}
        handleClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default OverviewList;
